// Checkers (American checkers / English draughts) — pure rules engine +
// computer player (no I/O; imports only the shared types), so it runs in the
// `games` edge function (Deno), in the browser (legal-move hints) and in tests.
//
// Rules:
//  - 8×8 board, play on the dark squares (a1 is dark). 12 men each.
//  - Seat 0 = Black (rows 0–2, moves first, toward row 7);
//    seat 1 = White (rows 5–7, toward row 0).
//  - Men move one square diagonally forward; kings one square in any
//    diagonal direction (no flying kings).
//  - Captures are mandatory. A capture jumps an adjacent enemy piece onto the
//    empty square beyond; a multi-jump must be completed (you may choose any
//    jumping route, not necessarily the longest). A piece can't be jumped twice.
//  - A man reaching the far row is crowned and the move ends there, even if
//    the new king could jump on.
//  - You lose when you can't move (no pieces, or all blocked).
//  - Draw by agreement, or after 40 moves each (80 plies) with no capture and
//    no man moving.
//
// Squares are 0..63 (row * 8 + col); the board is a 64-char string:
// '.' empty, 'b' / 'B' black man / king, 'w' / 'W' white man / king.
// A move is the whole path [from, hop1, hop2, …].

import { RuleError, type Ctx, type GameDef, type SeatResult } from './types.ts';
export { RuleError };

export const SEAT_NAMES = ['Black', 'White'];
export const QUIET_LIMIT = 80;
export const MAX_DRAW_OFFERS = 3;
export const IDLE_FORFEIT_MS = 10 * 60_000;

export type Level = 'easy' | 'medium' | 'hard';
export interface CheckersOptions { level: Level }
export const DEFAULT_OPTIONS: CheckersOptions = { level: 'medium' };

const rowOf = (s: number) => s >> 3;
const colOf = (s: number) => s & 7;
export const isDark = (s: number) => ((rowOf(s) + colOf(s)) & 1) === 0;
const sideOf = (ch: string): 0 | 1 | -1 => (ch === 'b' || ch === 'B' ? 0 : ch === 'w' || ch === 'W' ? 1 : -1);
const isKing = (ch: string) => ch === 'B' || ch === 'W';

export function startBoard(): string {
  let s = '';
  for (let i = 0; i < 64; i++) s += !isDark(i) ? '.' : rowOf(i) <= 2 ? 'b' : rowOf(i) >= 5 ? 'w' : '.';
  return s;
}

/** Standard 1–32 square number (square 1 on Black's back row). */
export const sqNum = (s: number) => rowOf(s) * 4 + (3 - (colOf(s) >> 1)) + 1;

export interface CMove { path: number[]; caps: number[] }

const DIRS_ALL: [number, number][] = [[1, -1], [1, 1], [-1, -1], [-1, 1]];
function dirsFor(ch: string): [number, number][] {
  if (isKing(ch)) return DIRS_ALL;
  return ch === 'b' ? [[1, -1], [1, 1]] : [[-1, -1], [-1, 1]];
}
const on = (r: number, c: number) => r >= 0 && r < 8 && c >= 0 && c < 8;
const crownRow = (side: number) => (side === 0 ? 7 : 0);

/** Every legal move for `side` (jumps only, if any jump exists). */
export function legalMoves(board: string, side: 0 | 1): CMove[] {
  const b = board.split('');
  const jumps: CMove[] = [];
  const steps: CMove[] = [];
  for (let s = 0; s < 64; s++) {
    const ch = b[s];
    if (sideOf(ch) !== side) continue;
    // Jumps: lift the piece (its square counts as empty), DFS over hops.
    b[s] = '.';
    const dfs = (at: number, path: number[], caps: number[]) => {
      let more = false;
      for (const [dr, dc] of dirsFor(ch)) {
        const r = rowOf(at), c = colOf(at);
        if (!on(r + 2 * dr, c + 2 * dc)) continue;
        const mid = (r + dr) * 8 + c + dc, land = (r + 2 * dr) * 8 + c + 2 * dc;
        const mc = b[mid];
        if (sideOf(mc) !== 1 - side || caps.includes(mid) || b[land] !== '.') continue;
        more = true;
        const np = [...path, land], nc = [...caps, mid];
        // Crowning ends the move.
        if (!isKing(ch) && rowOf(land) === crownRow(side)) jumps.push({ path: np, caps: nc });
        else dfs(land, np, nc);
      }
      if (!more && caps.length) jumps.push({ path, caps });
    };
    dfs(s, [s], []);
    b[s] = ch;
    if (jumps.length) continue;
    for (const [dr, dc] of dirsFor(ch)) {
      const r = rowOf(s) + dr, c = colOf(s) + dc;
      if (on(r, c) && b[r * 8 + c] === '.') steps.push({ path: [s, r * 8 + c], caps: [] });
    }
  }
  return jumps.length ? jumps : steps;
}

/** Board after a legal move (crowning included). */
export function applyPath(board: string, mv: CMove): string {
  const b = board.split('');
  const from = mv.path[0], to = mv.path[mv.path.length - 1];
  let ch = b[from];
  b[from] = '.';
  for (const c of mv.caps) b[c] = '.';
  const side = sideOf(ch);
  if (!isKing(ch) && rowOf(to) === crownRow(side)) ch = ch.toUpperCase();
  b[to] = ch;
  return b.join('');
}

export const notation = (mv: CMove) => mv.path.map(sqNum).join(mv.caps.length ? 'x' : '-');

// ---------------------------------------------------------------------------
// Game state
// ---------------------------------------------------------------------------

export type EndReason = 'no-moves' | 'resign' | 'agreement' | 'quiet' | 'abandoned';
export interface CheckersState {
  board: string;
  turn: 0 | 1;
  /** Move list in standard notation ("11-15", "22x15x8"). */
  moves: string[];
  /** The last move's path. */
  last: number[] | null;
  /** Plies since the last capture or man move (draw at QUIET_LIMIT). */
  quiet: number;
  result: null | { winner: 0 | 1 | null; reason: EndReason };
  drawOffer: 0 | 1 | null;
  offers: [number, number];
  level: Level;
}

export type CheckersMove =
  | { type: 'move'; path: number[] }
  | { type: 'resign' }
  | { type: 'offer_draw' }
  | { type: 'accept_draw' }
  | { type: 'decline_draw' };

export function newCheckers(opts?: Partial<CheckersOptions> | null): CheckersState {
  const o = { ...DEFAULT_OPTIONS, ...(opts ?? {}) };
  return { board: startBoard(), turn: 0, moves: [], last: null, quiet: 0, result: null, drawOffer: null, offers: [0, 0], level: o.level };
}

export function applyCheckersMove(g: CheckersState, seat: number, pathIn: unknown): { g: CheckersState; text: string } {
  if (g.result) throw new RuleError('The game is over');
  if (seat !== g.turn) throw new RuleError('Not your turn');
  if (!Array.isArray(pathIn) || pathIn.length < 2 || pathIn.length > 12 || !pathIn.every((x) => Number.isInteger(x) && x >= 0 && x < 64)) {
    throw new RuleError('Bad move');
  }
  const path = pathIn as number[];
  const legal = legalMoves(g.board, g.turn);
  const mv = legal.find((m) => m.path.length === path.length && m.path.every((s, i) => s === path[i]));
  if (!mv) {
    if (legal.some((m) => m.caps.length)) throw new RuleError(legal.some((m) => m.path[0] === path[0]) ? 'You must complete the jump' : 'You must jump');
    throw new RuleError('That move is not legal');
  }
  const wasMan = !isKing(g.board[mv.path[0]]);
  const board = applyPath(g.board, mv);
  const turn = (1 - g.turn) as 0 | 1;
  const quiet = mv.caps.length || wasMan ? 0 : g.quiet + 1;
  let result: CheckersState['result'] = null;
  if (!legalMoves(board, turn).length) result = { winner: g.turn, reason: 'no-moves' };
  else if (quiet >= QUIET_LIMIT) result = { winner: null, reason: 'quiet' };
  const text = notation(mv);
  return {
    text,
    g: { ...g, board, turn, moves: [...g.moves, text], last: mv.path, quiet, result, drawOffer: g.drawOffer === g.turn ? g.turn : null },
  };
}

export function resultText(g: CheckersState): string {
  const r = g.result;
  if (!r) return '';
  const w = r.winner === null ? '' : SEAT_NAMES[r.winner], l = r.winner === null ? '' : SEAT_NAMES[1 - r.winner];
  switch (r.reason) {
    case 'no-moves': return `${l} can't move — ${w} wins`;
    case 'resign': return `${l} resigned — ${w} wins`;
    case 'abandoned': return `${l} stopped moving — ${w} wins`;
    case 'agreement': return 'Draw agreed';
    case 'quiet': return '40 moves without a capture — draw';
  }
}

// ---------------------------------------------------------------------------
// Computer player: alpha-beta with a capture extension and a node budget.
// ---------------------------------------------------------------------------

const WIN = 100_000;

/** Static score from `side`'s point of view. */
export function evaluate(board: string, side: 0 | 1): number {
  let s = 0, pieces = 0;
  for (let i = 0; i < 64; i++) {
    const ch = board[i];
    const sd = sideOf(ch);
    if (sd < 0) continue;
    pieces++;
    const r = rowOf(i), c = colOf(i);
    let v: number;
    if (isKing(ch)) v = 170 - (Math.abs(3.5 - r) + Math.abs(3.5 - c)) * 3;
    else {
      const adv = sd === 0 ? r : 7 - r;
      v = 100 + adv * 4;
      if (adv === 0) v += 8;                       // back-row guard
      if (c >= 2 && c <= 5 && r >= 2 && r <= 5) v += 4; // centre
      if (c === 0 || c === 7) v -= 3;              // edge
    }
    s += sd === 0 ? v : -v;
  }
  void pieces;
  return side === 0 ? s : -s;
}

interface Search { nodes: number; limit: number; aborted: boolean }

function negamax(board: string, side: 0 | 1, depth: number, alpha: number, beta: number, ply: number, S: Search): number {
  if (++S.nodes > S.limit) { S.aborted = true; return 0; }
  const moves = legalMoves(board, side);
  if (!moves.length) return -WIN + ply;
  // Keep searching through captures at the horizon (no quiet stand-pat in a capture sequence).
  if (depth <= 0 && (!moves[0].caps.length || depth < -6)) return evaluate(board, side);
  moves.sort((a, b) => b.caps.length - a.caps.length);
  for (const m of moves) {
    const sc = -negamax(applyPath(board, m), (1 - side) as 0 | 1, depth - 1, -beta, -alpha, ply + 1, S);
    if (S.aborted) return 0;
    if (sc >= beta) return beta;
    if (sc > alpha) alpha = sc;
  }
  return alpha;
}

const LEVELS: Record<Level, { depth: number; limit: number; noise: number; random: number }> = {
  easy: { depth: 2, limit: 3_000, noise: 60, random: 0.2 },
  medium: { depth: 4, limit: 20_000, noise: 12, random: 0 },
  hard: { depth: 9, limit: 20_000, noise: 3, random: 0 },
};

export function chooseMove(board: string, side: 0 | 1, level: Level, rng: () => number): CMove {
  const L = LEVELS[level] ?? LEVELS.medium;
  const legal = legalMoves(board, side);
  if (!legal.length) throw new Error('No legal moves');
  if (legal.length === 1) return legal[0];
  if (L.random && rng() < L.random) return legal[Math.floor(rng() * legal.length)];
  const S: Search = { nodes: 0, limit: L.limit, aborted: false };
  const noise = legal.map(() => (rng() - 0.5) * 2 * L.noise);
  let order = legal.map((m, i) => i);
  let best = 0;
  for (let d = 1; d <= L.depth; d++) {
    const scored: [number, number][] = [];
    let alpha = -WIN * 2;
    for (const i of order) {
      let sc = -negamax(applyPath(board, legal[i]), (1 - side) as 0 | 1, d - 1, -WIN * 2, -alpha + L.noise * 2 + 1, 1, S);
      if (S.aborted) break;
      if (Math.abs(sc) < WIN / 2) sc += noise[i];
      scored.push([sc, i]);
      if (sc > alpha) alpha = sc;
    }
    if (S.aborted) break;
    scored.sort((a, b) => b[0] - a[0]);
    best = scored[0][1];
    order = scored.map((x) => x[1]);
    if (scored[0][0] > WIN / 2) break;
  }
  return legal[best];
}

export function botCheckersMove(g: CheckersState, seat: number, rng: () => number): CheckersMove {
  if (g.drawOffer === 1 - seat && evaluate(g.board, seat as 0 | 1) < -120) return { type: 'accept_draw' };
  return { type: 'move', path: chooseMove(g.board, seat as 0 | 1, g.level, rng).path };
}

// ---------------------------------------------------------------------------
// Table framework hookup (registered in registry.ts)
// ---------------------------------------------------------------------------

const nameAt = (ctx: Ctx, seat: number) => ctx.seats[seat]?.name || SEAT_NAMES[seat];

export const checkersGame: GameDef<CheckersState, CheckersMove> = {
  kind: 'checkers',
  label: 'Checkers',
  minPlayers: 2,
  maxPlayers: 2,
  seatNames: SEAT_NAMES,
  turnTimeoutMs: IDLE_FORFEIT_MS,
  configure: (cur, patch) => {
    const base: CheckersOptions = { ...DEFAULT_OPTIONS, ...(cur ?? {}) };
    if (!patch || typeof patch !== 'object') throw new RuleError('Bad options');
    const lv = (patch as any).level;
    if (lv !== undefined) {
      if (lv !== 'easy' && lv !== 'medium' && lv !== 'hard') throw new RuleError('Level must be easy, medium or hard');
      base.level = lv;
    }
    return base;
  },
  start: (_prev, ctx) => ({ g: newCheckers(ctx.options), note: 'Black moves first' }),
  turnSeat: (g) => (g.result ? null : g.turn),
  canAct: (g, actor) => !g.result && (actor === 0 || actor === 1),
  move: (g, _privs, actor, m, ctx) => {
    const type = (m as any)?.type;
    const me = nameAt(ctx, actor);
    if (type === 'move') {
      const r = applyCheckersMove(g, actor, (m as any).path);
      return { g: r.g, note: r.g.result ? resultText(r.g) : `${me} played ${r.text}` };
    }
    if (type === 'resign') return { g: { ...g, result: { winner: (1 - actor) as 0 | 1, reason: 'resign' }, drawOffer: null }, note: `${me} resigned` };
    if (type === 'offer_draw') {
      if (g.drawOffer === 1 - actor) return { g: { ...g, result: { winner: null, reason: 'agreement' }, drawOffer: null }, note: 'Draw agreed' };
      if (g.drawOffer === actor) throw new RuleError('Your draw offer is already on the table');
      if (g.offers[actor] >= MAX_DRAW_OFFERS) throw new RuleError('No more draw offers this game');
      const offers: [number, number] = [g.offers[0], g.offers[1]];
      offers[actor]++;
      return { g: { ...g, drawOffer: actor as 0 | 1, offers }, note: `${me} offers a draw` };
    }
    if (type === 'accept_draw') {
      if (g.drawOffer !== 1 - actor) throw new RuleError('There is no draw offer to accept');
      return { g: { ...g, result: { winner: null, reason: 'agreement' }, drawOffer: null }, note: 'Draw agreed' };
    }
    if (type === 'decline_draw') {
      if (g.drawOffer !== 1 - actor) throw new RuleError('There is no draw offer to decline');
      return { g: { ...g, drawOffer: null }, note: `${me} declined the draw` };
    }
    throw new RuleError('Unknown move');
  },
  bot: {
    move: (g, _priv, seat, ctx) => botCheckersMove(g, seat, ctx.rng),
    names: ['Crown Bot', 'Jump Bot'],
  },
  onIdle: (g, _privs, seat, ctx) => (g.result ? null : {
    g: { ...g, result: { winner: (1 - seat) as 0 | 1, reason: 'abandoned' }, drawOffer: null },
    note: `${nameAt(ctx, seat)} stopped moving — forfeit`,
  }),
  isOver: (g) => !!g.result,
  results: (g): SeatResult[] => [0, 1].map((seat) => ({
    seat,
    outcome: g.result?.winner === null || g.result?.winner === undefined ? 'draw' : g.result.winner === seat ? 'win' : 'loss',
    points: g.result?.winner === seat ? 1 : 0,
  })),
};
